import { XIcon } from "lucide-react";
import { useMemo, useState } from "react";

import RightCaret from "@/components/icons/right-caret-icon";
import { Button } from "@/components/ui/button";
import { formatDateDisplay } from "@/helpers/parsers";
import { cn } from "@/lib/utils";

import CopyCode from "./copy-code";
import { BlobLeft, BlobRight } from "./icons";

export interface CouponType {
  additionalDetails?: string;
  code: string;
  discountDollars?: number;
  discountDollarsScope?: string;
  discountPercentage?: number;
  expirationDate?: string;
  id: string;
  title?: string;
}

export const CouponScopeEnum = {
  EP: "off each person",
  TP: "off total price",
} as const;
export type CouponScopeEnum =
  (typeof CouponScopeEnum)[keyof typeof CouponScopeEnum];

function FlipCard({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "absolute inset-0 flex h-full items-center justify-center",
        className
      )}
    >
      <BlobLeft className="absolute bottom-0 left-0 md:hidden" />
      <BlobRight className="absolute top-0 right-0 md:hidden" />
      {children}
    </div>
  );
}

interface PromotionPanelProps {
  coupon: CouponType;
}

export default function PromotionPanel({ coupon }: PromotionPanelProps) {
  const [flip, setFlip] = useState(false);

  const handleToggle = () => setFlip((value) => !value);

  const { discount, expiration, scope } = useMemo(() => {
    let discount: string | null = null;
    if (coupon.discountDollars) {
      discount = Intl.NumberFormat("en-US", {
        currency: "USD",
        style: "currency",
        trailingZeroDisplay: "stripIfInteger",
      } as Intl.NumberFormatOptions).format(coupon.discountDollars);
    } else if (coupon.discountPercentage) {
      discount = Intl.NumberFormat("en-US", { style: "percent" }).format(
        coupon.discountPercentage
      );
    }

    return {
      discount,
      expiration: coupon.expirationDate
        ? formatDateDisplay(coupon.expirationDate)
        : null,
      scope:
        coupon.discountDollarsScope &&
        CouponScopeEnum[
          coupon.discountDollarsScope as keyof typeof CouponScopeEnum
        ],
    };
  }, [coupon]);

  return (
    <div
      className={cn(
        "first-of-type:ml-6 last-of-type:mr-6 md:last-of-type:mr-0 md:first-of-type:ml-0",
        "[&:nth-of-type(3n+1)>*]:bg-linear-to-r [&:nth-of-type(3n+1)>*]:from-deep-purple [&:nth-of-type(3n+1)>*]:to-pink-rose",
        "[&:nth-of-type(3n+2)>*]:bg-linear-71 [&:nth-of-type(3n+2)>*]:from-8% [&:nth-of-type(3n+2)>*]:from-aqua/80 [&:nth-of-type(3n+2)>*]:to-80% [&:nth-of-type(3n+2)>*]:to-dodger-blue/80",
        "[&:nth-of-type(3n+3)>*]:bg-linear-48 [&:nth-of-type(3n+3)>*]:from-15% [&:nth-of-type(3n+3)>*]:from-rose-red/80 [&:nth-of-type(3n+3)>*]:to-80% [&:nth-of-type(3n+3)>*]:to-raspberry-pink/80"
      )}
    >
      <div
        className={cn(
          "transform-3d perspective-distant relative h-60 w-80 overflow-hidden rounded-[10px] shadow-0-4-4 shadow-black/30 transition-transform md:h-21 md:w-full",
          flip && "rotate-y-180"
        )}
      >
        {/* front of card */}
        <FlipCard className={cn("z-2 transition-opacity", flip && "opacity-0")}>
          <div className="flex w-full flex-col gap-2 text-center md:flex-row md:px-5 md:text-left">
            <div className="md:py-5">
              <p className="font-semibold text-15 md:mb-[3px] md:font-normal md:text-14">
                {coupon.title}
              </p>
              <div
                className={cn(
                  "relative font-bold text-30 md:static md:inline md:font-semibold md:text-16",
                  "md:before-text-80 -before:top-1/2 before:absolute before:right-0 before:left-0 before:font-bold before:text-60 before:opacity-10 before:blur-[1px] before:content-[attr(data-discount)] md:before:top-0 md:before:right-auto md:before:text-80/[1em]"
                )}
                data-discount={discount}
              >
                {discount}
              </div>
              {scope && (
                <p className="text-14 md:ml-[3px] md:inline md:font-semibold md:text-16">
                  {scope}
                </p>
              )}
            </div>
            <div className="flex flex-col items-center justify-center gap-2.5 md:ml-auto">
              {coupon.code.toLowerCase() !== "automatic" && (
                <CopyCode>{coupon.code}</CopyCode>
              )}

              <Button
                className="h-7.5 rounded-full border-1 border-white/40 px-2 font-semibold text-13 text-white transition-opacity hover:opacity-50 md:h-auto md:self-end md:border-0 md:font-medium md:text-12"
                onClick={handleToggle}
              >
                Redemption details{" "}
                <RightCaret className="ml-[3px] hidden md:inline" />
              </Button>
            </div>
          </div>
        </FlipCard>
        {/* back of card */}
        <FlipCard
          className={cn(
            "relative rotate-y-180 rounded-xl transition-opacity md:rounded-[10px]",
            flip ? "z-2" : "opacity-0"
          )}
        >
          <div className="flex size-full flex-col justify-center gap-2 px-6.5 text-center md:px-4.5">
            <p className="text-14 text-white md:text-12">
              {coupon.additionalDetails}
            </p>
            {expiration && (
              <p className="text-14 text-white md:text-12">
                Expiration date: <strong>{expiration}</strong>
              </p>
            )}
            <Button
              className="absolute top-2 right-2 size-6.5 rounded-full bg-white p-2 text-pink-rose transition-opacity hover:opacity-50 md:size-4.5"
              onClick={handleToggle}
            >
              <XIcon className="size-4 md:size-3.5" />
            </Button>
          </div>
        </FlipCard>
      </div>
    </div>
  );
}
